import React, { useState, useEffect, useCallback } from 'react';
import { PayrollSession } from '../types/PayrollTypes';
import { GoogleDriveClient, GoogleDriveBackupInfo, preloadGoogleIdentityServices } from '../services/googleDrive';
import './GoogleDriveBackup.css';

interface GoogleDriveBackupProps {
  session: PayrollSession;
  onDataRestore: (restoredSession: PayrollSession) => void;
  className?: string;
}

const GoogleDriveBackup: React.FC<GoogleDriveBackupProps> = ({
  session,
  onDataRestore,
  className = ''
}) => {
  const [backupInfo, setBackupInfo] = useState<GoogleDriveBackupInfo>({ authenticated: false });
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');

  // バックアップ情報を取得（マウント時と各操作の後に明示的に呼ぶ。
  // session の変更（データ編集）では再取得しない）
  const loadBackupInfo = useCallback(async () => {
    try {
      const info = await GoogleDriveClient.getBackupInfo();
      setBackupInfo(info);
    } catch (error) {
      console.error('バックアップ情報取得エラー:', error);
    }
  }, []);

  useEffect(() => {
    loadBackupInfo();
  }, [loadBackupInfo]);

  // メッセージ表示
  const showMessage = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  // 日付フォーマット関数
  const formatLastModified = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '日時不明';
      }
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('日付フォーマットエラー:', error, 'dateString:', dateString);
      return '日時不明';
    }
  };

  // ファイルサイズフォーマット関数
  const formatFileSize = (bytes: number): string => {
    if (!isFinite(bytes) || bytes < 0) {
      return '不明';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Google Drive認証
  const handleAuthenticate = async () => {
    if (!session) {
      showMessage('セッションが初期化されていません', 'error');
      return;
    }

    setIsAuthenticating(true);
    try {
      await GoogleDriveClient.startAuthentication();
      await loadBackupInfo();
      showMessage('Google Drive認証が完了しました', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '認証に失敗しました';
      showMessage(errorMessage, 'error');
    } finally {
      setIsAuthenticating(false);
    }
  };

  // データバックアップ
  const handleBackup = async () => {
    if (!session) {
      showMessage('セッションが初期化されていません', 'error');
      return;
    }

    if (!backupInfo.authenticated) {
      showMessage('先にGoogle Drive認証を行ってください', 'error');
      return;
    }

    // バックアップの確認
    if (backupInfo.hasBackup) {
      const confirmed = window.confirm(
        '既存のバックアップデータを上書きしますか？\n' +
        '現在のデータでバックアップファイルが更新されます。'
      );
      if (!confirmed) return;
    }

    setIsBackingUp(true);
    try {
      const result = await GoogleDriveClient.backupData(session);
      
      if (result.success) {
        await loadBackupInfo(); // バックアップ情報を更新
        showMessage(result.message || 'バックアップが完了しました', 'success');
      } else {
        await loadBackupInfo(); // トークン失効時は認証状態を未認証へ戻す
        showMessage(result.error || 'バックアップに失敗しました', 'error');
      }
    } catch (error) {
      showMessage('バックアップ処理中にエラーが発生しました', 'error');
    } finally {
      setIsBackingUp(false);
    }
  };

  // データリストア
  const handleRestore = async () => {
    if (!session) {
      showMessage('セッションが初期化されていません', 'error');
      return;
    }

    if (!backupInfo.authenticated) {
      showMessage('先にGoogle Drive認証を行ってください', 'error');
      return;
    }

    if (!backupInfo.hasBackup) {
      showMessage('リストア可能なバックアップが見つかりません', 'error');
      return;
    }

    // リストアの確認
    const confirmed = window.confirm(
      '現在のデータがバックアップデータで置き換えられます。\n' +
      '続行しますか？'
    );
    if (!confirmed) return;

    setIsRestoring(true);
    try {
      const result = await GoogleDriveClient.restoreData();
      
      if (result.success && result.data) {
        onDataRestore(result.data);
        showMessage('データのリストアが完了しました', 'success');
      } else {
        await loadBackupInfo(); // トークン失効時は認証状態を未認証へ戻す
        showMessage(result.error || 'リストアに失敗しました', 'error');
      }
    } catch (error) {
      showMessage('リストア処理中にエラーが発生しました', 'error');
    } finally {
      setIsRestoring(false);
    }
  };

  // 認証解除
  const handleRevokeAuth = async () => {
    if (!session) return;

    const confirmed = window.confirm('Google Drive認証を解除しますか？');
    if (!confirmed) return;

    try {
      const result = await GoogleDriveClient.revokeAuthentication();
      // 失敗時もトークンは破棄済みのため、認証状態を同期して未認証表示へ戻す。
      await loadBackupInfo();
      if (result.success) {
        showMessage(result.message || '認証を解除しました', 'success');
      } else {
        showMessage(result.error || '認証解除に失敗しました', 'error');
      }
    } catch (error) {
      showMessage('認証解除処理中にエラーが発生しました', 'error');
    }
  };

  if (!session) {
    return null;
  }

  return (
    <div className={`google-drive-backup ${className}`}>
      <div className="backup-section">
        <h3 className="backup-title">
          <div>
            <span className="backup-icon">☁️ </span>
            Google Drive バックアップ
          </div>
          <div>
            <span className="backup-subtitle">
              給与データをバックアップ・リストアできます<br />その他のデータへのアクセス権限は不要です
            </span>
          </div>
        </h3>
        
        {message && (
          <div className={`backup-message backup-message-${messageType}`}>
            {message}
          </div>
        )}

        <div className="backup-status">
          {backupInfo.authenticated ? (
            <>
              <div className="auth-status auth-status-success">
                <span className="status-icon">✅</span>
                認証済み
              </div>

              {backupInfo.error && (
                <div className="backup-message backup-message-error">
                  バックアップ情報を取得できませんでした: {backupInfo.error}
                </div>
              )}

              {backupInfo.hasBackup && (
                <div className="backup-status-info">
                  <span className="status-icon">💾</span>
                  バックアップファイルが存在します
                  {backupInfo.lastModified && (
                    <div className="backup-date">
                      最終更新: {formatLastModified(backupInfo.lastModified)}
                    </div>
                  )}
                  {backupInfo.fileSize !== undefined && (
                    <div className="backup-date">
                      サイズ: {formatFileSize(backupInfo.fileSize)}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="auth-status auth-status-error">
              <span className="status-icon">❌</span>
              未認証
            </div>
          )}
        </div>

        <div className="backup-controls">
          {!backupInfo.authenticated ? (
            <button
              onClick={handleAuthenticate}
              onMouseEnter={preloadGoogleIdentityServices}
              onPointerDown={preloadGoogleIdentityServices}
              disabled={isAuthenticating}
              className="backup-btn backup-btn-primary"
            >
              {isAuthenticating ? (
                <>
                  <span className="loading-icon">🔄</span>
                  認証中...
                </>
              ) : (
                <>
                  <span className="btn-icon">🔑</span>
                  Google Drive認証
                </>
              )}
            </button>
          ) : (
            <>
              <div className="backup-actions">
                <button
                  onClick={handleBackup}
                  disabled={isBackingUp || isRestoring}
                  className="backup-btn backup-btn-backup"
                >
                  {isBackingUp ? (
                    <>
                      <span className="loading-icon">🔄</span>
                      バックアップ中...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">☁️</span>
                      バックアップ
                    </>
                  )}
                </button>

                <button
                  onClick={handleRestore}
                  disabled={isBackingUp || isRestoring || !backupInfo.hasBackup}
                  className="backup-btn backup-btn-restore"
                >
                  {isRestoring ? (
                    <>
                      <span className="loading-icon">🔄</span>
                      リストア中...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">📥</span>
                      リストア
                    </>
                  )}
                </button>
              </div>

              <button
                onClick={handleRevokeAuth}
                disabled={isBackingUp || isRestoring}
                className="backup-btn backup-btn-secondary backup-btn-small"
              >
                認証解除
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleDriveBackup;
